import { Grid } from '@mui/material';
import { ReactElement, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import KPI from '../../component/kpi/KPI';
import { getLastRefresh } from '../../redux/slices/app/appSlice';
import { getAssignmentKpis, getAssignmentKpisSelector } from '../../redux/slices/assignments/assignmentsSlice';
import { getDashboardRemediationKpis, getRemediationKpis, getRemediationKpisFetching } from '../../redux/slices/dashboard/dashboardSlice';
import { useDispatch } from '../../redux/store';
import { dayStart, viewerTimeZone } from './kpiWindow';

// The KPI row shown to Remediation organizations in place of the default
// Vehicles / Site Visits / Tools row. Four tiles:
//
//   Job Sites, Equipment            as of now
//   Equipment Movement, Job Sites   today (the viewer's local calendar day)
//
// The two activity tiles legitimately read 0 early in the morning, before the
// day's runs.
export const RemediationKpiRow = (): ReactElement => {
  const dispatch = useDispatch();
  const kpis = useSelector(getDashboardRemediationKpis);
  const fetching = useSelector(getRemediationKpisFetching);
  const assignmentKpis = useSelector(getAssignmentKpisSelector);
  // The global refresh button bumps lastRefresh; including it re-runs the fetch.
  const lastRefresh = useSelector(getLastRefresh);

  const windowStart = useMemo(() => dayStart(), []);
  const tz = useMemo(() => viewerTimeZone(), []);

  // Deliberately not routed through Dashboard's `lastRefresh !== lastFetch`
  // guard, which sets lastFetch once and would swallow this row's own refetch.
  useEffect(() => {
    dispatch(getRemediationKpis({ windowStart, tz }));
    // Prepared is the legacy Forced Delivery assignment count; it lives on a
    // separate endpoint from the remediation KPIs.
    dispatch(getAssignmentKpis());
  }, [dispatch, windowStart, tz, lastRefresh]);

  // Two by two on a phone, four across on a large screen. KPI tightens its labels
  // on a phone so each row fits a half-width tile.
  const size = { xs: 6, lg: 3 };

  return (
    <Grid container spacing={{ xs: 1, md: 6 }} sx={{ mb: 4 }}>
      <Grid item {...size}>
        <KPI
          title="Job Sites"
          data={[
            {
              amount: kpis?.sites_active,
              label: 'Active',
              color: kpis?.sites_active ? 'blue' : 'black',
              link: '/activity/pipeline',
            },
            {
              amount: kpis?.sites_ready_for_pickup,
              label: 'Ready',
              color: kpis?.sites_ready_for_pickup ? 'blue' : 'black',
              link: '/activity/pipeline',
            },
            {
              amount: kpis?.sites_scheduled_for_pickup,
              label: 'Scheduled',
              color: 'black',
              link: '/activity/pipeline',
            },
          ]}
          fetching={fetching}
        />
      </Grid>

      <Grid item {...size}>
        <KPI
          title="Equip Location"
          data={[
            {
              amount: kpis?.equipment_onsite,
              label: 'On-JobSite',
              color: kpis?.equipment_onsite ? 'blue' : 'black',
              link: '/tools/items?scanStatus=2,3&active=true',
            },
            {
              amount: kpis?.equipment_on_road,
              label: 'On-Road',
              color: 'black',
              link: '/tools/items?scanStatus=1&active=true',
            },
            {
              amount: kpis?.equipment_at_home,
              label: 'At-Home',
              color: 'black',
              link: '/tools/items?scanStatus=0&active=true',
            },
          ]}
          fetching={fetching}
        />
      </Grid>

      <Grid item {...size}>
        <KPI
          title="Equip Movement"
          data={[
            {
              amount: kpis?.movement_delivered,
              label: 'Delivered',
              color: 'black',
              link: '/activity/tool-usage?activityBegin=today&activityTypeIds=1000',
            },
            {
              amount: kpis?.movement_recovered,
              label: 'Recovered',
              color: 'black',
              link: '/activity/tool-usage?activityBegin=today&activityTypeIds=210',
            },
            // Same-day round trips: delivered to a site and brought back later
            // that day. Counted here instead of in Delivered and Recovered.
            {
              amount: kpis?.movement_cancelled,
              label: 'Returns',
              color: 'black',
            },
            // Recovered within two hours of delivery: a mis-click or retry.
            // Counted here instead of in Delivered and Recovered.
            {
              amount: kpis?.movement_noise,
              label: 'Cancellations',
              color: 'black',
            },
          ]}
          fetching={fetching}
        />
      </Grid>

      {/* Counts site/days, not sites: one site recovering on two days counts
          twice, and Closed is a subset of Recovered. Prepared is the legacy
          assignment count and is a plain current-state number, not a site/day. */}
      <Grid item {...size}>
        <KPI
          title="Job Site Activity"
          data={[
            {
              amount: assignmentKpis?.prepared_cnt,
              label: 'Prepared',
              color: 'black',
              link: '/delivery/prepared',
            },
            {
              amount: kpis?.sitedays_delivered,
              label: 'Delivered',
              color: 'black',
              link: '/delivery/delivered',
            },
            {
              amount: kpis?.sitedays_recovered,
              label: 'Recovered',
              color: 'black',
              link: '/customers/recovery-progress',
            },
            {
              amount: kpis?.sitedays_closed,
              label: 'Closed',
              color: kpis?.sitedays_closed ? 'green' : 'black',
              link: '/customers/closed-job-site',
            },
          ]}
          fetching={fetching}
        />
      </Grid>
    </Grid>
  );
};
